import * as React from "react";
import StartupSplash from "./StartupSplash";
import Login from "../../Login/Login";
import ErrorBoundary from "../ErrorBoundary";
import { LogClientDiagnostic } from "source/app/services/ClientDiagnostics";
import {
    CheckClientSessionAsync,
    SubscribeToSessionExpiry,
} from "source/app/services/Authentication/ClientSession";

type SessionState = "checking" | "authenticated" | "login" | "expired" | "unavailable";

export default function SessionGate({ children }: { children: React.ReactNode }) {
    const [state, setState] = React.useState<SessionState>("checking");
    const [attempt, setAttempt] = React.useState(0);
    const hadStoredUsername = React.useRef(
        Boolean(localStorage.getItem("username"))
    );

    React.useEffect(
        () =>
            SubscribeToSessionExpiry(() =>
                setState(hadStoredUsername.current ? "expired" : "login")
            ),
        []
    );
    React.useEffect(() => {
        if (state !== "checking") return;
        const controller = new AbortController();
        const timeout = window.setTimeout(() => controller.abort(), 15000);
        let mounted = true;
        void CheckClientSessionAsync(controller.signal)
            .then((authenticated) => {
                if (!mounted) return;
                if (authenticated) {
                    hadStoredUsername.current = true;
                    setState("authenticated");
                    return;
                }

                setState(hadStoredUsername.current ? "expired" : "login");
            })
            .catch(() => {
                if (!mounted) return;
                LogClientDiagnostic("client-session", "check-unavailable", {}, "error");
                setState("unavailable");
            })
            .finally(() => window.clearTimeout(timeout));
        return () => {
            mounted = false;
            window.clearTimeout(timeout);
            controller.abort();
        };
    }, [state, attempt]);

    if (state === "authenticated") return <>{children}</>;
    if (state === "login" || state === "expired") {
        return <ErrorBoundary>
            {state === "expired" && <p role="status">Your session has ended. Please sign in again.</p>}
            <Login />
        </ErrorBoundary>;
    }
    if (state === "checking") return <StartupSplash completed={2} />;
    return <div role="status">
        <p>Unable to connect. Please try again.</p>
        {state === "unavailable" && <button onClick={() => {
            setAttempt((value) => value + 1);
            setState("checking");
        }}>Retry</button>}
    </div>;
}
